import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CaretRight, MagnifyingGlass, X } from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { IconButton } from 'src/components/IconButton';
import { Input } from 'src/components/Input';
import { getInsightTypeLabel } from 'src/components/insights/constants';
import { Modal } from 'src/components/Modal';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { can, Subject } from 'src/hooks/usePermissions';
import { Insight } from 'src/interfaces/agent.interface';
import { useInsights } from 'src/pages/Agents/contexts/InsightsContext';
import { cn } from 'src/utils/className';
import { getErrorMessage } from 'src/utils/requestUtils';

type SelectExistingInsightsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (insights: Insight[]) => void;
  excludeIds: string[];
};

export const SelectExistingInsightsModal: React.FC<
  SelectExistingInsightsModalProps
> = ({ isOpen, onClose, onAdd, excludeIds }) => {
  const { role } = useEnvironment();
  const { listInsights, deleteInsight, getAgentCountForInsight } =
    useInsights();
  const notification = useNotification();

  const [allInsights, setAllInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [insightsFetchVersion, setInsightsFetchVersion] = useState(0);
  const debouncedQuery = useDebounce(query, { wait: 300 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Insight | null>(null);
  const [deleteAgentCount, setDeleteAgentCount] = useState<number | null>(null);
  const [isDeleteAgentCountLoading, setIsDeleteAgentCountLoading] =
    useState(false);
  const [deleteAgentCountFailed, setDeleteAgentCountFailed] = useState(false);
  const [isDeletingInsight, setIsDeletingInsight] = useState(false);
  const [descriptionOverflowById, setDescriptionOverflowById] = useState<
    Record<string, boolean>
  >({});

  const deleteCountRequestIdRef = useRef(0);
  const descriptionRefById = useRef<Record<string, HTMLSpanElement | null>>({});

  const canDeleteInsights = can(role, 'delete', Subject.INSIGHTS);

  // Fetch insights when modal opens or debounced query changes
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        const items = await listInsights(debouncedQuery || undefined);
        if (!cancelled) {
          setAllInsights(items);
        }
      } catch {
        if (!cancelled) {
          notification.error('Failed to load insights');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    debouncedQuery,
    listInsights,
    notification,
    insightsFetchVersion,
  ]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setInsightsFetchVersion(0);
      setSelectedIds(new Set());
      setExpandedIds(new Set());
      setDeleteTarget(null);
      setDeleteAgentCount(null);
      setDeleteAgentCountFailed(false);
      setIsDeleteAgentCountLoading(false);
      setIsDeletingInsight(false);
      setDescriptionOverflowById({});
    }
  }, [isOpen]);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);

  const filteredInsights = useMemo(
    () => allInsights.filter((i) => !excludeSet.has(i.id)),
    [allInsights, excludeSet],
  );

  // Measure description truncation after paint so clientWidth/scrollWidth reflect final layout.
  useEffect(() => {
    if (!isOpen || filteredInsights.length === 0) return;

    const rafId = window.requestAnimationFrame(() => {
      setDescriptionOverflowById((prev) => {
        const next = { ...prev };
        let changed = false;

        filteredInsights.forEach((insight) => {
          const descriptionNode = descriptionRefById.current[insight.id];
          if (!descriptionNode) return;

          // Add a small tolerance for sub-pixel rounding differences.
          const hasOverflow =
            descriptionNode.scrollWidth > descriptionNode.clientWidth + 1;
          if (next[insight.id] !== hasOverflow) {
            next[insight.id] = hasOverflow;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [filteredInsights, isOpen]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const setDescriptionRef = useCallback(
    (id: string) => (element: HTMLSpanElement | null) => {
      descriptionRefById.current[id] = element;
    },
    [],
  );

  const handleAdd = useCallback(() => {
    const selectedInsights = allInsights.filter((i) => selectedIds.has(i.id));
    onAdd(selectedInsights);
  }, [allInsights, selectedIds, onAdd]);

  const handleCloseDeleteConfirm = useCallback(() => {
    deleteCountRequestIdRef.current += 1;
    setDeleteTarget(null);
    setDeleteAgentCount(null);
    setDeleteAgentCountFailed(false);
    setIsDeleteAgentCountLoading(false);
    setIsDeletingInsight(false);
  }, []);

  const handleOpenDeleteConfirm = useCallback(
    (insight: Insight) => {
      const requestId = deleteCountRequestIdRef.current + 1;
      deleteCountRequestIdRef.current = requestId;

      setDeleteTarget(insight);
      setDeleteAgentCount(null);
      setDeleteAgentCountFailed(false);
      setIsDeleteAgentCountLoading(true);

      void (async () => {
        try {
          const count = await getAgentCountForInsight(insight.id);
          if (deleteCountRequestIdRef.current !== requestId) return;
          setDeleteAgentCount(count);
        } catch (error) {
          if (deleteCountRequestIdRef.current !== requestId) return;
          setDeleteAgentCountFailed(true);
          notification.error(
            getErrorMessage(error, 'Failed to load insight agent usage count.'),
          );
        } finally {
          if (deleteCountRequestIdRef.current === requestId) {
            setIsDeleteAgentCountLoading(false);
          }
        }
      })();
    },
    [getAgentCountForInsight, notification],
  );

  const handleDeleteInsight = useCallback(() => {
    if (!deleteTarget || isDeletingInsight) return;

    const deletedInsightId = deleteTarget.id;
    setIsDeletingInsight(true);
    void (async () => {
      try {
        await deleteInsight(deletedInsightId);
        setAllInsights((prev) =>
          prev.filter((insight) => insight.id !== deletedInsightId),
        );
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(deletedInsightId);
          return next;
        });
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.delete(deletedInsightId);
          return next;
        });
        setDescriptionOverflowById((prev) => {
          const next = { ...prev };
          delete next[deletedInsightId];
          return next;
        });
        delete descriptionRefById.current[deletedInsightId];
        setInsightsFetchVersion((prev) => prev + 1);
        handleCloseDeleteConfirm();
      } catch (error) {
        notification.error(getErrorMessage(error, 'Failed to delete insight.'));
      } finally {
        setIsDeletingInsight(false);
      }
    })();
  }, [
    deleteInsight,
    deleteTarget,
    handleCloseDeleteConfirm,
    isDeletingInsight,
    notification,
  ]);

  if (deleteTarget) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCloseDeleteConfirm}
        title="Delete Insight"
        showClose
        showModalBorder
        className="lg:max-w-3xl"
        footer={
          <div className="flex items-center justify-between gap-4 pt-4 px-6 pb-6 border-t border-neutral-200">
            <Button
              variant="outlined"
              size="small"
              onClick={handleCloseDeleteConfirm}
              disabled={isDeletingInsight}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              onClick={handleDeleteInsight}
              disabled={isDeleteAgentCountLoading || isDeletingInsight}
            >
              Delete Insight
            </Button>
          </div>
        }
      >
        <div className="my-6 rounded-md border-l-4 border-warning-400 bg-warning-100 px-4 py-3">
          <div className="text-sm font-semibold text-primary-body">
            Are you sure you want to delete{' '}
            {deleteTarget.label
              ? `"${deleteTarget.label}" (${deleteTarget.id})`
              : deleteTarget.id}
            ?
          </div>
          <div className="mt-1 text-sm text-neutral-600">
            {isDeleteAgentCountLoading ? (
              <span className="inline-flex items-center gap-2">
                <span>This insight is used by</span>
                <span className="h-4 w-16 animate-pulse rounded bg-neutral-200" />
                <span>.</span>
              </span>
            ) : deleteAgentCountFailed ? (
              <span>This action cannot be undone.</span>
            ) : deleteAgentCount && deleteAgentCount > 0 ? (
              <span>
                This insight is used by{' '}
                <span className="font-semibold text-primary-body">
                  {deleteAgentCount}{' '}
                  {deleteAgentCount === 1 ? 'agent' : 'agents'}
                </span>
                . This action cannot be undone.
              </span>
            ) : (
              <span>This action cannot be undone.</span>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Existing Insights"
      subtitle="Select one or more insights to add to this agent."
      showClose
      showModalBorder
      className="lg:max-w-3xl"
      footer={
        <div className="flex items-center justify-between gap-4 pt-4 px-6 pb-6 border-t border-neutral-200">
          <div className="flex items-center gap-3 text-sm text-neutral-500">
            <span>{selectedIds.size} selected</span>
            {selectedIds.size > 0 && (
              <Button
                variant="text"
                color="primary"
                size="small"
                className="px-0 py-0 text-sm hover:bg-transparent active:bg-transparent hover:underline"
                onClick={clearSelection}
              >
                Clear selection
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outlined" onClick={onClose} size="small">
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={selectedIds.size === 0}
              size="small"
            >
              Add selected
            </Button>
          </div>
        </div>
      }
    >
      <div className="py-4 space-y-3">
        <Input
          placeholder="Search insights by description or ID"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          icon={<MagnifyingGlass className="w-4 h-4" />}
          iconPosition="left"
        />

        <div className="rounded-lg border border-neutral-200 bg-neutral-0 overflow-hidden">
          <div className="max-h-80 overflow-y-auto">
            {loading && filteredInsights.length === 0 ? (
              <div className="p-6 text-sm text-neutral-400 text-center">
                Loading insights...
              </div>
            ) : filteredInsights.length === 0 ? (
              <div className="p-6 text-sm text-neutral-400 text-center">
                {query
                  ? 'No matching insights.'
                  : 'No additional insights available to add.'}
              </div>
            ) : (
              filteredInsights.map((insight) => {
                const checked = selectedIds.has(insight.id);
                const expanded = expandedIds.has(insight.id);

                return (
                  <div
                    key={insight.id}
                    className={cn(
                      'border-b border-neutral-200 last:border-b-0',
                      checked && 'bg-primary-100',
                    )}
                  >
                    <div className="flex items-start gap-3 px-4 py-3 hover:bg-neutral-50 transition-colors">
                      <div className="mt-0.5 flex-shrink-0">
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelected(insight.id)}
                          ariaLabel={`Select ${insight.label || insight.id}`}
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <Button
                          type="button"
                          variant="text"
                          color="default"
                          size="small"
                          className="h-auto min-w-0 w-full justify-start rounded-none px-0 py-0 text-left hover:bg-transparent active:bg-transparent"
                          onClick={() => toggleSelected(insight.id)}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="truncate font-medium text-sm text-neutral-700">
                              {insight.label || insight.id}
                            </span>
                            <span className="truncate text-xs text-neutral-400">
                              {insight.id}
                            </span>
                            <span className="flex-shrink-0 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs text-neutral-600">
                              {getInsightTypeLabel(insight.type)}
                            </span>
                          </div>
                        </Button>

                        {expanded ? (
                          <div className="mt-1 text-xs text-neutral-400 leading-snug">
                            <span>{insight.instructions}</span>{' '}
                            <Button
                              type="button"
                              variant="text"
                              color="primary"
                              size="small"
                              className="inline-flex h-auto gap-1 px-0 py-0 text-xs hover:bg-transparent active:bg-transparent hover:underline"
                              onClick={() => toggleExpanded(insight.id)}
                            >
                              Less
                              <CaretRight className="h-3 w-3 rotate-90" />
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-1 flex min-w-0 items-center gap-1.5">
                            <span
                              ref={setDescriptionRef(insight.id)}
                              className="truncate text-xs text-neutral-400"
                            >
                              {insight.instructions}
                            </span>
                            {descriptionOverflowById[insight.id] && (
                              <Button
                                type="button"
                                variant="text"
                                color="primary"
                                size="small"
                                className="h-auto flex-shrink-0 gap-1 px-0 py-0 text-xs hover:bg-transparent active:bg-transparent hover:underline"
                                onClick={() => toggleExpanded(insight.id)}
                              >
                                More
                                <CaretRight className="h-3 w-3" weight="bold" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>

                      {canDeleteInsights && (
                        <IconButton
                          aria-label={`Delete insight ${insight.label || insight.id}`}
                          className="mt-0.5 h-7 w-7 text-neutral-300 hover:text-error"
                          onClick={() => handleOpenDeleteConfirm(insight)}
                        >
                          <X className="h-4 w-4" />
                        </IconButton>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
